import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { toast, Slide as ToastSlide } from "react-toastify";
import { client } from "../../../../services/axiosClient";

/**
 * Estado e lógica do modal Special Options (batch: recalcular, reset, reprocess, lock).
 * `refreshInputsStatus` pode mudar a cada render; usamos ref no polling para evitar stale closure.
 */
export function useSpecialOptionsModal({
  monthlyStatus,
  refreshInputsStatus,
  showUpdateDemoTab = false,
}) {
  const { t, i18n } = useTranslation();

  const [specialOptionsModalOpen, setSpecialOptionsModalOpen] = useState(false);
  const [specialOptionsTab, setSpecialOptionsTab] = useState(0);
  const [specialOptionsStartMonth, setSpecialOptionsStartMonth] =
    useState(null);
  const [specialOptionsEndMonth, setSpecialOptionsEndMonth] = useState(null);
  const [specialOptionsStep, setSpecialOptionsStep] = useState("select");
  const [specialOptionsProgress, setSpecialOptionsProgress] = useState(0);
  const [specialOptionsStatus, setSpecialOptionsStatus] = useState("");
  const [specialOptionsJobId, setSpecialOptionsJobId] = useState(null);
  const [specialOptionsError, setSpecialOptionsError] = useState(null);
  const [activeBatchJob, setActiveBatchJob] = useState(null);
  const [isPollingBatch, setIsPollingBatch] = useState(false);
  const specialOptionsPollingRef = useRef(null);
  const [specialOptionsSelectedInput, setSpecialOptionsSelectedInput] =
    useState(null);
  const [specialOptionsSelectedInputs, setSpecialOptionsSelectedInputs] =
    useState([]);
  const [reprocessInputsList, setReprocessInputsList] = useState([]);
  const [specialOptionsRecalcLogistics, setSpecialOptionsRecalcLogistics] =
    useState(true);
  const [specialOptionsResetMode, setSpecialOptionsResetMode] =
    useState("soft");
  const [reprocessMode, setReprocessMode] = useState("existing");
  const [broadcastFile, setBroadcastFile] = useState(null);
  const [broadcastUploading, setBroadcastUploading] = useState(false);
  const broadcastFileInputRef = useRef(null);
  const [lockStatus, setLockStatus] = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockReleasing, setLockReleasing] = useState(false);
  const [processedPeriodsLoading, setProcessedPeriodsLoading] = useState(false);
  const [multiMonthFiles, setMultiMonthFiles] = useState([]);
  const [multiMonthAiMapping, setMultiMonthAiMapping] = useState([]);
  const [multiMonthAiLoading, setMultiMonthAiLoading] = useState(false);
  const [multiMonthUploadProgress, setMultiMonthUploadProgress] = useState({
    current: 0,
    total: 0,
    currentFile: "",
    staging: false,
  });
  const multiMonthFolderRef = useRef(null);
  const multiMonthFileRef = useRef(null);

  const refreshRef = useRef(refreshInputsStatus);
  refreshRef.current = refreshInputsStatus;

  const getDate = useCallback(
    (val, format_in, format_out = format_in) => {
      moment.locale(i18n.language);
      return moment(val, format_in).format(format_out).replace(".", "");
    },
    [i18n.language],
  );

  useEffect(() => {
    return () => {
      if (specialOptionsPollingRef.current) {
        clearInterval(specialOptionsPollingRef.current);
      }
    };
  }, []);

  const getSpecialOptionsMonthsList = useCallback(() => {
    return [...monthlyStatus].sort((a, b) => {
      const dateA = a.year * 100 + a.month;
      const dateB = b.year * 100 + b.month;
      return dateA - dateB;
    });
  }, [monthlyStatus]);

  const PIPELINE_READY_MONTH_STATUSES = ["complete", "success"];

  const getLocalPipelineProcessedPeriods = useCallback(() => {
    return getSpecialOptionsMonthsList()
      .filter((m) => PIPELINE_READY_MONTH_STATUSES.includes(m.status))
      .map((m) => ({ year: m.year, month: m.month }));
  }, [getSpecialOptionsMonthsList]);

  const formatMonthLabel = useCallback(
    (item) => {
      if (!item) return "";
      return getDate(
        (item.year * 100 + item.month).toString(),
        "YYYYMM",
        "MMM-YY",
      );
    },
    [getDate],
  );

  const applySpecialOptionsProcessedPeriods = useCallback(
    (periods) => {
      if (!periods.length) {
        toast.error(
          t("modeling_inputs_v2.batch_operations.no_processed_periods"),
          { transition: ToastSlide, autoClose: 4000 },
        );
        return;
      }
      const monthsSorted = getSpecialOptionsMonthsList();
      const findMonth = (y, mo) =>
        monthsSorted.find((x) => x.year === y && x.month === mo);
      const first = findMonth(periods[0].year, periods[0].month);
      const lastP = periods[periods.length - 1];
      const last = findMonth(lastP.year, lastP.month);
      if (!first || !last) {
        toast.error(
          t("modeling_inputs_v2.batch_operations.no_processed_periods"),
          { transition: ToastSlide, autoClose: 4000 },
        );
        return;
      }
      setSpecialOptionsStartMonth(first);
      setSpecialOptionsEndMonth(last);
      toast.success(
        t(
          "modeling_inputs_v2.batch_operations.select_processed_periods_applied",
          {
            start: formatMonthLabel(first),
            end: formatMonthLabel(last),
          },
        ),
        { transition: ToastSlide, autoClose: 3500 },
      );
    },
    [formatMonthLabel, getSpecialOptionsMonthsList, t],
  );

  const handleSelectProcessedPeriods = useCallback(() => {
    if (specialOptionsTab === 0 || specialOptionsTab === 1) {
      applySpecialOptionsProcessedPeriods(getLocalPipelineProcessedPeriods());
      return;
    }
    if (specialOptionsTab === 4) {
      return;
    }

    let inputIds = [];
    if (reprocessMode === "existing") {
      inputIds = [
        ...new Set(specialOptionsSelectedInputs.map((i) => i.input_id)),
      ];
    } else if (specialOptionsSelectedInput) {
      inputIds = [specialOptionsSelectedInput.input_id];
    }

    setProcessedPeriodsLoading(true);
    client
      .post("api/batch_calculation/processed-periods/", {
        input_ids: inputIds,
      })
      .then((response) => {
        applySpecialOptionsProcessedPeriods(response.data?.periods || []);
      })
      .catch((error) => {
        console.error("Error fetching processed periods:", error);
        toast.error(
          t(
            "modeling_inputs_v2.batch_operations.select_processed_periods_error",
          ),
          { transition: ToastSlide, autoClose: 4000 },
        );
      })
      .finally(() => {
        setProcessedPeriodsLoading(false);
      });
  }, [
    applySpecialOptionsProcessedPeriods,
    getLocalPipelineProcessedPeriods,
    reprocessMode,
    specialOptionsSelectedInput,
    specialOptionsSelectedInputs,
    specialOptionsTab,
    t,
  ]);

  const fetchReprocessInputsList = useCallback(() => {
    client
      .get("api/batch_calculation/inputs/")
      .then((response) => {
        setReprocessInputsList(response.data.groups || []);
      })
      .catch((error) => {
        console.error("Error fetching reprocess inputs list:", error);
        setReprocessInputsList([]);
      });
  }, []);

  const fetchLockStatus = useCallback(() => {
    setLockLoading(true);
    client
      .get("api/implementation_lock/status/")
      .then((response) => {
        setLockStatus(response.data);
      })
      .catch((error) => {
        console.error("Error fetching lock status:", error);
        setLockStatus(null);
      })
      .finally(() => setLockLoading(false));
  }, []);

  const handleReleaseLock = useCallback(() => {
    setLockReleasing(true);
    client
      .post("api/implementation_lock/release/")
      .then(() => {
        setLockStatus({ locked: false, lock_info: null });
        toast.success(
          t("modeling_inputs_v2.batch_operations.remove_lock_success"),
          { transition: ToastSlide, autoClose: 3000 },
        );
      })
      .catch((error) => {
        console.error("Error releasing lock:", error);
      })
      .finally(() => setLockReleasing(false));
  }, [t]);

  const startBatchPolling = useCallback(
    (jobId) => {
      if (specialOptionsPollingRef.current) {
        clearInterval(specialOptionsPollingRef.current);
      }

      const poll = () => {
        setIsPollingBatch(true);
        client
          .get(`api/batch_calculation/${jobId}/`)
          .then((response) => {
            const data = response.data;
            const progress =
              data.total_count > 0
                ? Math.round((data.processed_count / data.total_count) * 100)
                : 0;

            setSpecialOptionsProgress(progress);
            setActiveBatchJob(data);

            if (data.current_year && data.current_month) {
              const currentPeriod = getDate(
                (data.current_year * 100 + data.current_month).toString(),
                "YYYYMM",
                "MMM-YY",
              );
              const inputLabel = data.current_input_name
                ? `${data.current_input_name} - `
                : "";
              setSpecialOptionsStatus(
                t("modeling_inputs_v2.batch_operations.processing", {
                  period: `${inputLabel}${currentPeriod}`,
                  current: data.processed_count + 1,
                  total: data.total_count,
                }),
              );
            }

            if (data.status === "COMPLETED") {
              clearInterval(specialOptionsPollingRef.current);
              specialOptionsPollingRef.current = null;
              setSpecialOptionsStatus(
                t("modeling_inputs_v2.batch_operations.completed", {
                  processed: data.processed_count,
                  total: data.total_count,
                }),
              );
              if (data.operation_type === "INSIGHTS_DEMO_FILLUP") {
                toast.success(
                  t(
                    "modeling_inputs_v2.batch_operations.update_demo_completed_toast",
                  ),
                  { transition: ToastSlide, autoClose: 5000 },
                );
              }
              setActiveBatchJob(null);
              refreshRef.current(true);
            } else if (data.status === "FAILED") {
              clearInterval(specialOptionsPollingRef.current);
              specialOptionsPollingRef.current = null;
              setSpecialOptionsError(
                data.error_message ||
                  t("modeling_inputs_v2.batch_operations.operation_failed"),
              );
              setSpecialOptionsStatus(
                t("modeling_inputs_v2.batch_operations.error"),
              );
              setActiveBatchJob(null);
            } else if (data.status === "CANCELLED") {
              clearInterval(specialOptionsPollingRef.current);
              specialOptionsPollingRef.current = null;
              setSpecialOptionsStatus(
                t("modeling_inputs_v2.batch_operations.cancelled"),
              );
              setActiveBatchJob(null);
            }
          })
          .catch((error) => {
            console.error("Error polling batch status:", error);
          })
          .finally(() => {
            setIsPollingBatch(false);
          });
      };

      poll();
      specialOptionsPollingRef.current = setInterval(poll, 5000);
    },
    [getDate, t],
  );

  const _submitBatchJob = useCallback(
    (params) => {
      client
        .post("api/batch_calculation/", params)
        .then((response) => {
          const jobId = response.data.job_id;
          setSpecialOptionsJobId(jobId);
          setActiveBatchJob({
            job_id: jobId,
            operation_type: params.operation_type,
            start_year: params.start_year,
            start_month: params.start_month,
            end_year: params.end_year,
            end_month: params.end_month,
            status: "PENDING",
            processed_count: 0,
            total_count: response.data.total_count,
          });
          startBatchPolling(jobId);
        })
        .catch((error) => {
          console.error("Error creating batch job:", error);
          const errorMsg =
            error.response?.data?.error ||
            t("modeling_inputs_v2.batch_operations.error_creating_operation");
          setSpecialOptionsError(errorMsg);
          setSpecialOptionsStatus(
            t("modeling_inputs_v2.batch_operations.error"),
          );
        });
    },
    [startBatchPolling, t],
  );

  const getMonthsInRange = useCallback(() => {
    if (!specialOptionsStartMonth || !specialOptionsEndMonth) return [];
    const months = [];
    let y = specialOptionsStartMonth.year;
    let m = specialOptionsStartMonth.month;
    const endVal =
      specialOptionsEndMonth.year * 100 + specialOptionsEndMonth.month;
    while (y * 100 + m <= endVal) {
      months.push({
        year: y,
        month: m,
        label: formatMonthLabel({ year: y, month: m }),
      });
      m++;
      if (m > 12) {
        m = 1;
        y++;
      }
    }
    return months;
  }, [formatMonthLabel, specialOptionsEndMonth, specialOptionsStartMonth]);

  const handleMultiMonthAiMatch = useCallback(() => {
    setSpecialOptionsStep("ai_matching");
    setMultiMonthAiLoading(true);
    setMultiMonthAiMapping([]);
    setSpecialOptionsError(null);

    const monthsInRange = getMonthsInRange();
    const monthLabels = monthsInRange.map((m) => m.label);
    const filenames = multiMonthFiles.map((f) => f.name);

    client
      .post("api/chat/file-month-match/", {
        filenames,
        months: monthLabels,
        input_name: specialOptionsSelectedInput?.meta_name || "",
      })
      .then((response) => {
        const matches = response.data?.matches || [];
        const mapping = multiMonthFiles.map((file) => {
          const match = matches.find((m) => m.filename === file.name);
          const matchedMonth = match?.month || null;
          const monthObj = matchedMonth
            ? monthsInRange.find((m) => m.label === matchedMonth)
            : null;
          return {
            file,
            month: monthObj || null,
            confidence: match?.confidence || "none",
          };
        });
        setMultiMonthAiMapping(mapping);
      })
      .catch((error) => {
        console.error("Error calling AI file-month match:", error);
        const fallback = multiMonthFiles.map((file) => ({
          file,
          month: null,
          confidence: "none",
        }));
        setMultiMonthAiMapping(fallback);
        setSpecialOptionsError(
          t("modeling_inputs_v2.batch_operations.multi_month_ai_error"),
        );
      })
      .finally(() => {
        setMultiMonthAiLoading(false);
      });
  }, [getMonthsInRange, multiMonthFiles, specialOptionsSelectedInput, t]);

  const handleMultiMonthConfirmMapping = useCallback(() => {
    setSpecialOptionsStep("confirm");
  }, []);

  const handleMultiMonthExecute = useCallback(async () => {
    setSpecialOptionsStep("progress");
    setSpecialOptionsError(null);
    setSpecialOptionsProgress(0);
    setSpecialOptionsStatus(
      t("modeling_inputs_v2.batch_operations.multi_month_uploading_progress", {
        current: 0,
        total: 0,
      }),
    );

    const validItems = multiMonthAiMapping.filter((item) => item.month);
    setMultiMonthUploadProgress({
      current: 0,
      total: validItems.length,
      currentFile: "",
      staging: true,
    });

    const uploadedFiles = [];
    for (let i = 0; i < validItems.length; i++) {
      const item = validItems[i];
      setMultiMonthUploadProgress((prev) => ({
        ...prev,
        current: i + 1,
        currentFile: item.file.name,
      }));

      try {
        const formData = new FormData();
        formData.append("file", item.file);
        formData.append("input_id", specialOptionsSelectedInput.input_id);
        const uploadRes = await client.post(
          "api/batch_calculation/upload-broadcast-file/",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        uploadedFiles.push({
          year: item.month.year,
          month: item.month.month,
          broadcast_file_path: uploadRes.data.broadcast_file_path,
        });
      } catch (err) {
        console.error("Error staging file:", item.file.name, err);
        setSpecialOptionsError(
          t("modeling_inputs_v2.batch_operations.multi_month_staging_error", {
            filename: item.file.name,
          }),
        );
        return;
      }
    }

    setMultiMonthUploadProgress((prev) => ({ ...prev, staging: false }));

    const years_months = uploadedFiles.map((f) => f.year * 100 + f.month);
    const minYM = Math.min(...years_months);
    const maxYM = Math.max(...years_months);

    setSpecialOptionsStatus(
      t("modeling_inputs_v2.batch_operations.starting_reprocess"),
    );

    _submitBatchJob({
      operation_type: "MULTI_MONTH_UPLOAD",
      input_id: specialOptionsSelectedInput.input_id,
      multi_month_files: uploadedFiles,
      start_year: Math.floor(minYM / 100),
      start_month: minYM % 100,
      end_year: Math.floor(maxYM / 100),
      end_month: maxYM % 100,
    });
  }, [_submitBatchJob, multiMonthAiMapping, specialOptionsSelectedInput, t]);

  const handleSpecialOptionsExecute = useCallback(() => {
    setSpecialOptionsStep("progress");
    setSpecialOptionsProgress(0);
    setSpecialOptionsError(null);

    if (specialOptionsTab === 4) {
      setSpecialOptionsStatus(
        t("modeling_inputs_v2.batch_operations.update_demo_starting"),
      );
      // Period range is discovered server-side from Food Brands' completed
      // PipelineControl rows, so the demo always covers every period the
      // mirror source has — no client-side calendar to drift.
      _submitBatchJob({ operation_type: "INSIGHTS_DEMO_FILLUP" });
      return;
    }

    const statusMessages = {
      0: t("modeling_inputs_v2.batch_operations.starting_recalculation"),
      1: t("modeling_inputs_v2.batch_operations.starting_reset"),
      2:
        reprocessMode === "broadcast"
          ? t("modeling_inputs_v2.batch_operations.starting_broadcast")
          : t("modeling_inputs_v2.batch_operations.starting_reprocess"),
    };
    setSpecialOptionsStatus(statusMessages[specialOptionsTab]);

    const getOperationType = () => {
      if (specialOptionsTab === 2 && reprocessMode === "broadcast")
        return "BROADCAST";
      return { 0: "RECALCULATE", 1: "RESET", 2: "REPROCESS" }[
        specialOptionsTab
      ];
    };
    const params = {
      operation_type: getOperationType(),
      start_year: specialOptionsStartMonth.year,
      start_month: specialOptionsStartMonth.month,
      end_year: specialOptionsEndMonth.year,
      end_month: specialOptionsEndMonth.month,
    };

    if (specialOptionsTab === 0) {
      params.recalc_logistics = specialOptionsRecalcLogistics;
    }

    if (specialOptionsTab === 1) {
      params.reset_mode = specialOptionsResetMode;
    }

    if (specialOptionsTab === 2) {
      if (
        reprocessMode === "existing" &&
        specialOptionsSelectedInputs.length > 0
      ) {
        params.input_ids = specialOptionsSelectedInputs.map((i) => i.input_id);
      } else if (specialOptionsSelectedInput) {
        params.input_id = specialOptionsSelectedInput.input_id;
      }
    }

    if (
      specialOptionsTab === 2 &&
      reprocessMode === "broadcast" &&
      broadcastFile &&
      specialOptionsSelectedInput
    ) {
      setSpecialOptionsStatus(
        t("modeling_inputs_v2.batch_operations.uploading_broadcast_file"),
      );
      setBroadcastUploading(true);
      const formData = new FormData();
      formData.append("file", broadcastFile);
      formData.append("input_id", specialOptionsSelectedInput.input_id);
      client
        .post("api/batch_calculation/upload-broadcast-file/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((uploadResponse) => {
          setBroadcastUploading(false);
          params.broadcast_file_path = uploadResponse.data.broadcast_file_path;
          _submitBatchJob(params);
        })
        .catch((error) => {
          setBroadcastUploading(false);
          console.error("Error uploading broadcast file:", error);
          const errorMsg =
            error.response?.data?.error ||
            t("modeling_inputs_v2.batch_operations.error_uploading_file");
          setSpecialOptionsError(errorMsg);
          setSpecialOptionsStatus(
            t("modeling_inputs_v2.batch_operations.error"),
          );
        });
      return;
    }

    _submitBatchJob(params);
  }, [
    _submitBatchJob,
    broadcastFile,
    reprocessMode,
    specialOptionsEndMonth,
    specialOptionsRecalcLogistics,
    specialOptionsResetMode,
    specialOptionsSelectedInput,
    specialOptionsSelectedInputs,
    specialOptionsStartMonth,
    specialOptionsTab,
    t,
  ]);

  const handleCancelBatchJob = useCallback(() => {
    if (!specialOptionsJobId) return;

    client
      .post(`api/batch_calculation/${specialOptionsJobId}/cancel/`)
      .then(() => {
        setSpecialOptionsStatus(
          t("modeling_inputs_v2.batch_operations.cancelling"),
        );
      })
      .catch((error) => {
        console.error("Error cancelling batch job:", error);
      });
  }, [specialOptionsJobId, t]);

  const checkActiveBatchJob = useCallback(() => {
    client
      .get(`api/batch_calculation/active/`)
      .then((response) => {
        const activeJob = response.data.active_job;
        if (activeJob) {
          setActiveBatchJob(activeJob);
          setSpecialOptionsJobId(activeJob.job_id);

          const startMonth = monthlyStatus.find(
            (m) =>
              m.year === activeJob.start_year &&
              m.month === activeJob.start_month,
          );
          const endMonth = monthlyStatus.find(
            (m) =>
              m.year === activeJob.end_year && m.month === activeJob.end_month,
          );

          setSpecialOptionsStartMonth(startMonth || null);
          setSpecialOptionsEndMonth(endMonth || null);
          const tabMap = {
            RECALCULATE: 0,
            RESET: 1,
            REPROCESS: 2,
            BROADCAST: 2,
            MULTI_MONTH_UPLOAD: 2,
            INSIGHTS_DEMO_FILLUP: 4,
          };
          setSpecialOptionsTab(tabMap[activeJob.operation_type] ?? 0);
          setSpecialOptionsStep("progress");

          const progress =
            activeJob.total_count > 0
              ? Math.round(
                  (activeJob.processed_count / activeJob.total_count) * 100,
                )
              : 0;
          setSpecialOptionsProgress(progress);

          if (activeJob.current_year && activeJob.current_month) {
            const currentPeriod = getDate(
              (
                activeJob.current_year * 100 +
                activeJob.current_month
              ).toString(),
              "YYYYMM",
              "MMM-YY",
            );
            setSpecialOptionsStatus(
              t("modeling_inputs_v2.batch_operations.processing", {
                period: currentPeriod,
                current: activeJob.processed_count + 1,
                total: activeJob.total_count,
              }),
            );
          } else {
            setSpecialOptionsStatus(
              t("modeling_inputs_v2.batch_operations.waiting_to_start"),
            );
          }

          startBatchPolling(activeJob.job_id);
        }
      })
      .catch((error) => {
        console.error("Error checking active batch job:", error);
      });
  }, [getDate, monthlyStatus, startBatchPolling, t]);

  const isMonthInBatchRange = useCallback(
    (monthData) => {
      if (!activeBatchJob || !monthData) return false;
      const monthValue = monthData.year * 100 + monthData.month;
      const startValue =
        activeBatchJob.start_year * 100 + activeBatchJob.start_month;
      const endValue = activeBatchJob.end_year * 100 + activeBatchJob.end_month;
      return monthValue >= startValue && monthValue <= endValue;
    },
    [activeBatchJob],
  );

  const handleSpecialOptionsBack = useCallback(() => {
    if (
      specialOptionsStep === "confirm" &&
      specialOptionsTab === 2 &&
      reprocessMode === "multi_month"
    ) {
      setSpecialOptionsStep("ai_matching");
    } else if (specialOptionsStep === "ai_matching") {
      setSpecialOptionsStep("select");
    } else {
      setSpecialOptionsStep("select");
    }
    setSpecialOptionsError(null);
  }, [reprocessMode, specialOptionsStep, specialOptionsTab]);

  const handleCloseSpecialOptionsModal = useCallback(() => {
    setSpecialOptionsModalOpen(false);
    setMultiMonthFiles([]);
    setMultiMonthAiMapping([]);
    setMultiMonthAiLoading(false);
    setMultiMonthUploadProgress({
      current: 0,
      total: 0,
      currentFile: "",
      staging: false,
    });
  }, []);

  const getSpecialOptionsRangeText = useCallback(() => {
    const start = formatMonthLabel(specialOptionsStartMonth);
    const end = formatMonthLabel(specialOptionsEndMonth);
    return `${start} até ${end}`;
  }, [formatMonthLabel, specialOptionsEndMonth, specialOptionsStartMonth]);

  const isValidMonthRange = useCallback(() => {
    if (!specialOptionsStartMonth || !specialOptionsEndMonth) return false;
    const startDate =
      specialOptionsStartMonth.year * 100 + specialOptionsStartMonth.month;
    const endDate =
      specialOptionsEndMonth.year * 100 + specialOptionsEndMonth.month;
    return startDate <= endDate;
  }, [specialOptionsEndMonth, specialOptionsStartMonth]);

  const isValidSpecialOptionsSelection = useCallback(() => {
    if (specialOptionsTab === 4) return true;
    if (!isValidMonthRange()) return false;
    if (specialOptionsTab === 2) {
      if (reprocessMode === "existing") {
        if (specialOptionsSelectedInputs.length === 0) return false;
      } else if (reprocessMode === "multi_month") {
        if (!specialOptionsSelectedInput) return false;
        if (multiMonthFiles.length === 0) return false;
      } else {
        if (!specialOptionsSelectedInput) return false;
        if (!broadcastFile) return false;
      }
    }
    return true;
  }, [
    broadcastFile,
    isValidMonthRange,
    multiMonthFiles.length,
    reprocessMode,
    specialOptionsSelectedInput,
    specialOptionsSelectedInputs.length,
    specialOptionsTab,
  ]);

  const handleSpecialOptionsConfirm = useCallback(() => {
    if (specialOptionsTab === 2 && reprocessMode === "multi_month") {
      handleMultiMonthAiMatch();
      return;
    }
    setSpecialOptionsStep("confirm");
  }, [handleMultiMonthAiMatch, reprocessMode, specialOptionsTab]);

  const handleOpenSpecialOptionsModal = useCallback(() => {
    const openDefaultSelect = () => {
      const months = getSpecialOptionsMonthsList();
      if (months.length > 0 && !specialOptionsStartMonth) {
        setSpecialOptionsStartMonth(months[0]);
      }
      if (months.length > 0 && !specialOptionsEndMonth) {
        setSpecialOptionsEndMonth(months[months.length - 1]);
      }
      setSpecialOptionsStep("select");
      setSpecialOptionsProgress(0);
      setSpecialOptionsStatus("");
      setSpecialOptionsError(null);
    };

    const finishOpen = () => {
      fetchReprocessInputsList();
      setSpecialOptionsModalOpen(true);
    };

    if (
      activeBatchJob &&
      ["PENDING", "RUNNING"].includes(activeBatchJob.status)
    ) {
      const tabMap = {
        RECALCULATE: 0,
        RESET: 1,
        REPROCESS: 2,
        BROADCAST: 2,
        MULTI_MONTH_UPLOAD: 2,
        INSIGHTS_DEMO_FILLUP: 4,
      };
      setSpecialOptionsTab(tabMap[activeBatchJob.operation_type] ?? 0);
      const startMonth = monthlyStatus.find(
        (m) =>
          m.year === activeBatchJob.start_year &&
          m.month === activeBatchJob.start_month,
      );
      const endMonth = monthlyStatus.find(
        (m) =>
          m.year === activeBatchJob.end_year &&
          m.month === activeBatchJob.end_month,
      );
      setSpecialOptionsStartMonth(startMonth || null);
      setSpecialOptionsEndMonth(endMonth || null);
      setSpecialOptionsStep("progress");
      finishOpen();
      return;
    }

    openDefaultSelect();
    finishOpen();
  }, [
    activeBatchJob,
    fetchReprocessInputsList,
    getSpecialOptionsMonthsList,
    monthlyStatus,
    specialOptionsEndMonth,
    specialOptionsStartMonth,
  ]);

  return {
    activeBatchJob,
    isMonthInBatchRange,
    handleOpenSpecialOptionsModal,
    specialOptionsModalOpen,
    checkActiveBatchJob,
    specialOptionsTab,
    setSpecialOptionsTab,
    specialOptionsStep,
    setSpecialOptionsStep,
    specialOptionsStartMonth,
    setSpecialOptionsStartMonth,
    specialOptionsEndMonth,
    setSpecialOptionsEndMonth,
    specialOptionsProgress,
    specialOptionsStatus,
    specialOptionsError,
    specialOptionsJobId,
    specialOptionsSelectedInput,
    setSpecialOptionsSelectedInput,
    specialOptionsSelectedInputs,
    setSpecialOptionsSelectedInputs,
    reprocessInputsList,
    specialOptionsRecalcLogistics,
    setSpecialOptionsRecalcLogistics,
    specialOptionsResetMode,
    setSpecialOptionsResetMode,
    reprocessMode,
    setReprocessMode,
    broadcastFile,
    setBroadcastFile,
    broadcastUploading,
    broadcastFileInputRef,
    lockStatus,
    lockLoading,
    lockReleasing,
    processedPeriodsLoading,
    multiMonthFiles,
    setMultiMonthFiles,
    multiMonthAiMapping,
    setMultiMonthAiMapping,
    multiMonthAiLoading,
    setMultiMonthAiLoading,
    multiMonthUploadProgress,
    multiMonthFolderRef,
    multiMonthFileRef,
    getSpecialOptionsMonthsList,
    handleSelectProcessedPeriods,
    fetchLockStatus,
    handleReleaseLock,
    handleCloseSpecialOptionsModal,
    handleSpecialOptionsConfirm,
    handleSpecialOptionsBack,
    handleSpecialOptionsExecute,
    handleMultiMonthExecute,
    handleMultiMonthConfirmMapping,
    handleCancelBatchJob,
    getSpecialOptionsRangeText,
    isValidMonthRange,
    isValidSpecialOptionsSelection,
    getMonthsInRange,
    isPollingBatch,
    showUpdateDemoTab,
    i18n,
    t,
  };
}
